// src/components/TemplatesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Grid, Paper, Typography, Box, Card, CardContent,
    IconButton, Button, Chip, Alert, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch,
    FormControlLabel, Skeleton
} from '@mui/material';
import {
    TextSnippet, Add, Edit, Delete, ContentCopy
} from '@mui/icons-material';
import axiosInstance from '../api/axiosInstance';
import PageHeader from './common/PageHeader';

const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
};

const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.6
};

const CATEGORY_OPTIONS = [
    { value: 'greeting', label: 'ترحيب', color: 'success' },
    { value: 'farewell', label: 'وداع', color: 'info' },
    { value: 'support', label: 'دعم فني', color: 'warning' },
    { value: 'sales', label: 'مبيعات', color: 'secondary' },
    { value: 'general', label: 'عام', color: 'default' },
];

export default function TemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: 'general',
        shortcut: '',
        is_active: true
    });

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/chatbot/templates/');
            setTemplates(response.data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            setError('فشل في تحميل القوالب');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const handleOpenDialog = (template = null) => {
        if (template) {
            setFormData({
                title: template.title,
                content: template.content,
                category: template.category,
                shortcut: template.shortcut || '',
                is_active: template.is_active
            });
            setSelectedTemplate(template);
        } else {
            setFormData({
                title: '',
                content: '',
                category: 'general',
                shortcut: '',
                is_active: true
            });
            setSelectedTemplate(null);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedTemplate(null);
    };

    const handleSubmit = async () => {
        try {
            if (selectedTemplate) {
                await axiosInstance.put(`/chatbot/templates/${selectedTemplate.id}/`, formData);
            } else {
                await axiosInstance.post('/chatbot/templates/', formData);
            }
            handleCloseDialog();
            fetchTemplates();
        } catch (err) {
            console.error('Error saving template:', err);
            setError('فشل في حفظ القالب');
        }
    };

    const handleDelete = async (templateId) => {
        if (window.confirm('هل أنت متأكد من حذف هذا القالب؟')) {
            try {
                await axiosInstance.delete(`/chatbot/templates/${templateId}/`);
                fetchTemplates();
            } catch (err) {
                console.error('Error deleting template:', err);
                setError('فشل في حذف القالب');
            }
        }
    };

    const handleCopyContent = (content) => {
        navigator.clipboard.writeText(content);
    };

    const getCategoryInfo = (category) => {
        return CATEGORY_OPTIONS.find(c => c.value === category) || CATEGORY_OPTIONS[4];
    };

    const TemplateCard = ({ template }) => {
        const categoryInfo = getCategoryInfo(template.category);

        return (
            <Card
                component={motion.div}
                whileHover={{ y: -4 }}
                sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextSnippet color="primary" />
                            <Typography variant="h6" fontWeight="600" noWrap>
                                {template.title}
                            </Typography>
                        </Box>
                        <Box>
                            <IconButton size="small" onClick={() => handleCopyContent(template.content)} title="نسخ المحتوى">
                                <ContentCopy fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenDialog(template)} sx={{ color: 'primary.main' }}>
                                <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(template.id)} sx={{ color: 'error.main' }}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Chip
                        label={categoryInfo.label}
                        color={categoryInfo.color}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    {template.shortcut && (
                        <Chip
                            label={template.shortcut}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2, ml: 1 }}
                        />
                    )}

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: 60
                        }}
                    >
                        {template.content}
                    </Typography>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <Box>
                <Box sx={{ mb: 4 }}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
                    <Skeleton variant="text" width="60%" height={32} />
                </Box>
                <Grid container spacing={3}>
                    {[1, 2, 3, 4].map((item) => (
                        <Grid key={item} item xs={12} md={6} lg={4}>
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <motion.div
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
        >
            <Box>
                <PageHeader
                    title="قوالب الرسائل"
                    subtitle="إنشاء وإدارة قوالب الردود السريعة للمحادثات"
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="600">
                        القوالب ({templates.length})
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{ borderRadius: 2 }}
                    >
                        إضافة قالب جديد
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {templates.map((template) => (
                        <Grid key={template.id} item xs={12} md={6} lg={4}>
                            <TemplateCard template={template} />
                        </Grid>
                    ))}
                </Grid>

                {templates.length === 0 && (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <TextSnippet sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            لا توجد قوالب
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            ابدأ بإضافة قالب جديد للردود السريعة
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                        >
                            إضافة قالب جديد
                        </Button>
                    </Paper>
                )}

                {/* Add/Edit Template Dialog */}
                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {selectedTemplate ? 'تعديل القالب' : 'إضافة قالب جديد'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 1 }}>
                            <TextField
                                fullWidth
                                label="عنوان القالب"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="محتوى الرسالة"
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>التصنيف</InputLabel>
                                <Select
                                    value={formData.category}
                                    label="التصنيف"
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    {CATEGORY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="الاختصار"
                                value={formData.shortcut}
                                onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                                helperText="اختصار سريع مثل /hello أو /thanks"
                                sx={{ mb: 2 }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                }
                                label="نشط"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            إلغاء
                        </Button>
                        <Button onClick={handleSubmit} variant="contained">
                            {selectedTemplate ? 'تحديث' : 'إضافة'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </motion.div>
    );
}
